'use client';

import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md';

const BASE =
  'pressable inline-flex items-center justify-center gap-2 rounded-full font-semibold ' +
  'select-none disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none ' +
  'focus-visible:ring-2 focus-visible:ring-strong focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-10 px-5 text-sm',
};

const VARIANTS: Record<Variant, string> = {
  primary: 'hero-glass is-button text-fg-inverse',
  secondary: 'bg-surface text-fg-primary shadow-sm hover:shadow-md',
  ghost: 'bg-transparent text-fg-secondary hover:bg-raised',
  destructive: 'bg-surface text-negative shadow-sm ring-1 ring-inset ring-negative/40',
};

/**
 * Compose the canonical button classes. Lets non-button elements (like
 * `<Link>`) wear the same skin: `<Link className={buttonClasses('primary')}>`.
 */
export function buttonClasses(
  variant: Variant = 'secondary',
  size: ButtonSize = 'md',
  extra = '',
): string {
  return `${BASE} ${SIZES[size]} ${VARIANTS[variant]} ${extra}`.trim();
}

/**
 * Canonical button — DESIGN.md §8.3.
 *
 * - `primary`     : glass hero/start tint + glow. **One per screen** — the main action.
 * - `secondary`   : white surface, soft shadow.
 * - `ghost`       : transparent, muted text. Toolbar / tertiary.
 * - `destructive` : white surface with negative-coloured text + ring.
 *
 * Loading state replaces the label with a 3-dot pulse, never a spinner ring
 * (DESIGN.md §13).
 */
export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  children,
  className,
  disabled,
  ...rest
}: {
  variant?: Variant;
  size?: ButtonSize;
  loading?: boolean;
  children: ReactNode;
} & ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={buttonClasses(variant, size, className ?? '')}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? <DotPulse /> : children}
    </button>
  );
}

function DotPulse() {
  return (
    <span aria-label="Loading" className="inline-flex items-end gap-1">
      <Dot delay="0ms" />
      <Dot delay="120ms" />
      <Dot delay="240ms" />
    </span>
  );
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="inline-block h-1.5 w-1.5 rounded-full bg-current"
      style={{ animation: `flowlet-dot-pulse 0.9s ease-in-out ${delay} infinite` }}
    />
  );
}
